// src/views/Employees/Modal/EmployeeModal.jsx
import React, { useEffect, useState } from "react";
import { Modal, Button } from "react-bootstrap";
import { useForm, Controller } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { employeeActions } from "../redux/actions/employee.actions";
import { modeConstants } from "../constants";
import PhoneInput from "react-phone-input-2";

/**
 * DocUploadField
 * – existingUrl : full URL of the already-saved document (from DB)
 * – newFile     : File object the user just picked
 * – onFileChange: setter for newFile
 * – label       : field label
 * – inputId     : unique id for <input>
 * – disabled    : read-only (view mode)
 */
function DocUploadField({ existingUrl, newFile, onFileChange, label, inputId, disabled }) {
  const [blobUrl, setBlobUrl] = useState(null);

  useEffect(() => {
    return () => { if (blobUrl) URL.revokeObjectURL(blobUrl); };
  }, [blobUrl]);

  const handleChange = (e) => {
    const f = e.target.files?.[0] ?? null;
    if (blobUrl) URL.revokeObjectURL(blobUrl);
    setBlobUrl(f ? URL.createObjectURL(f) : null);
    onFileChange(f);
  };

  const viewUrl  = blobUrl || existingUrl || null;
  const hasDoc   = !!viewUrl;
  const isNew    = !!blobUrl;
  const hasSaved = !!existingUrl && !isNew;

  return (
    <div
      className="mb-2"
      style={{
        border: `1.5px solid ${isNew ? "#198754" : hasSaved ? "#0d6efd" : "#dee2e6"}`,
        borderRadius: 10,
        padding: "10px 12px",
        background: isNew ? "#f0fdf4" : hasSaved ? "#f0f6ff" : "#fafafa",
        transition: "border-color 0.2s, background 0.2s",
      }}
    >
      {/* Header row */}
      <div className="d-flex align-items-center justify-content-between gap-2 flex-wrap">
        <div className="d-flex align-items-center gap-2">
          {/* Doc type icon */}
          <div
            style={{
              width: 34, height: 34, borderRadius: 8,
              background: isNew ? "#198754" : hasSaved ? "#0d6efd" : "#adb5bd",
              display: "flex", alignItems: "center", justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <i
              className={`bi ${isNew ? "bi-file-earmark-check-fill" : hasSaved ? "bi-file-earmark-text-fill" : "bi-file-earmark-plus"} text-white`}
              style={{ fontSize: 16 }}
            />
          </div>

          {/* Label + status */}
          <div style={{ lineHeight: 1.3 }}>
            <div className="fw-semibold" style={{ fontSize: 13 }}>{label}</div>
            <div style={{ fontSize: 11 }}>
              {isNew ? (
                <span className="text-success">
                  New:{" "}
                  <span
                    title={newFile?.name}
                    style={{ maxWidth: 120, display: "inline-block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", verticalAlign: "bottom" }}
                  >
                    {newFile?.name}
                  </span>
                </span>
              ) : hasSaved ? (
                <span className="text-primary">Saved document</span>
              ) : (
                <span className="text-muted">No document uploaded</span>
              )}
            </div>
          </div>
        </div>

        {/* Action buttons */}
        <div className="d-flex gap-1 flex-shrink-0">
          {hasDoc && (
            <a
              href={viewUrl}
              target="_blank"
              rel="noopener noreferrer"
              className={`btn btn-sm ${isNew ? "btn-success" : "btn-primary"}`}
              title={isNew ? "Preview selected file" : "View saved document"}
              style={{ fontSize: 12, padding: "3px 10px" }}
            >
              <i className={`bi ${isNew ? "bi-eye" : "bi-eye-fill"} me-1`} />
              {isNew ? "Preview" : "View"}
            </a>
          )}
          {isNew && (
            <button
              type="button"
              className="btn btn-sm btn-outline-danger"
              title="Remove new file"
              style={{ fontSize: 12, padding: "3px 8px" }}
              onClick={() => {
                if (blobUrl) URL.revokeObjectURL(blobUrl);
                setBlobUrl(null);
                onFileChange(null);
              }}
            >
              <i className="bi bi-x-lg" />
            </button>
          )}
        </div>
      </div>

      {/* File input */}
      {!disabled && (
        <div className="mt-2">
          <input
            id={inputId}
            type="file"
            accept=".pdf,image/*"
            className="form-control form-control-sm"
            style={{ fontSize: 12 }}
            onChange={handleChange}
          />
          <div className="text-muted mt-1" style={{ fontSize: 10 }}>
            {hasSaved ? "Choose a new file to replace the existing document" : "Upload PDF or image"}
          </div>
        </div>
      )}
    </div>
  );
}

const DEFAULT_VALUES = {
  fullName: "",
  email: "",
  phone_number: "",
  passport_number: "",
  passport_issue_date: "",
  passport_expiry_date: "",
  visa_type: "",
  visa_number: "",
  visa_issue_date: "",
  visa_expiry_date: "",
  biometric_residence_permit_number: "",
  brp_expiry_date: "",
  national_insurance_number: "",
  contract_start_date: "",
  contract_end_date: "",
  code: "",
  address: "",
};

export default function EmployeeModal({
  show,
  onHide,
  mode, // "add" | "edit" | "view"
}) {
  const [countryCode, setCountryCode] = useState("gb");
  const isAdd = mode === modeConstants.ADD_MODE;
  const isEdit = mode === modeConstants.UPDATE_MODE;
  const isView = mode === modeConstants.VIEW_MODE;
  const dispatch = useDispatch();
  const {
    register,
    handleSubmit,
    reset,
    setValue,
    control,
    formState: { errors },
  } = useForm({
    defaultValues: { defaultValues: DEFAULT_VALUES },
  });

  const [profilePicFile, setProfilePicFile] = useState(null);
  const [passportDocFile, setPassportDocFile] = useState(null);
  const [visaDocFile, setVisaDocFile] = useState(null);
  const [brpDocFile, setBrpDocFile] = useState(null);
  const [contractDocFile, setContractDocFile] = useState(null);
  const [previewPic, setPreviewPic] = useState("");
  const initialData = useSelector((s) => s.employeeReducer.formData);

  // load initial data in edit/view
  useEffect(() => {
    if (!show) return;

    if (isAdd) {
      reset(DEFAULT_VALUES);
      setProfilePicFile(null);
      setPassportDocFile(null);
      setVisaDocFile(null);
      setBrpDocFile(null);
      setContractDocFile(null);
      setPreviewPic("");
      return;
    }
    // clear new-file selections when switching to edit (keep existing URLs from initialData)
    setPassportDocFile(null);
    setVisaDocFile(null);
    setBrpDocFile(null);
    setContractDocFile(null);

    if (initialData && (isEdit || isView)) {
      reset({
        fullName: initialData.fullName ?? "",
        code: initialData.code ?? "",
        email: initialData.email ?? "",
        phone_number: String(initialData.phone_number ?? "") ?? "",
        passport_number: initialData.passport_number ?? "",
        passport_issue_date: initialData.passport_issue_date ?? "",
        passport_expiry_date: initialData.passport_expiry_date ?? "",
        visa_type: initialData.visa_type ?? "",
        visa_number: initialData.visa_number ?? "",
        visa_issue_date: initialData.visa_issue_date ?? "",
        visa_expiry_date: initialData.visa_expiry_date ?? "",
        biometric_residence_permit_number:
          initialData.biometric_residence_permit_number ?? "",
        brp_expiry_date: initialData.brp_expiry_date ?? "",
        national_insurance_number: initialData.national_insurance_number ?? "",
        contract_start_date: initialData.contract_start_date ?? "",
        contract_end_date: initialData.contract_end_date ?? "",
        address: initialData.address ?? "",
      });
    }
    // show existing image if present
    if (initialData.profile_pic_url) {
      // if backend returns full url, just use it
      setPreviewPic(
        process.env.REACT_APP_STORAGE_URL + initialData.profile_pic_url
      );
    } else {
      setPreviewPic("");

      // files won't be prefilled (browser security)
      setProfilePicFile(null);
      setPassportDocFile(null);
      setVisaDocFile(null);
      setBrpDocFile(null);
      setContractDocFile(null);
    }
  }, [show, isAdd, isEdit, isView, initialData, reset]);

  const onSubmit = async (values) => {
    const fd = new FormData();
    fd.append("fullName", values.fullName || "");
    fd.append("email", values.email || "");
    fd.append("phone_number", values.phone_number || "");
    fd.append("passport_number", values.passport_number || "");
    fd.append("passport_issue_date", values.passport_issue_date || "");
    fd.append("passport_expiry_date", values.passport_expiry_date || "");
    fd.append("visa_type", values.visa_type || "");
    fd.append("visa_number", values.visa_number || "");
    fd.append("visa_issue_date", values.visa_issue_date || "");
    fd.append("visa_expiry_date", values.visa_expiry_date || "");
    fd.append("code", values.code || "");
    fd.append("is_employee", 1 || "");
    fd.append(
      "biometric_residence_permit_number",
      values.biometric_residence_permit_number || ""
    );
    fd.append("brp_expiry_date", values.brp_expiry_date || "");
    fd.append(
      "national_insurance_number",
      values.national_insurance_number || ""
    );
    fd.append("contract_start_date", values.contract_start_date || "");
    fd.append("contract_end_date", values.contract_end_date || "");
    fd.append("address", values.address || "");

    // files
    if (profilePicFile instanceof File) {
      fd.append("profile_pic", profilePicFile, profilePicFile.name);
    }
    if (passportDocFile instanceof File) {
      fd.append("passport_document", passportDocFile, passportDocFile.name);
    }
    if (visaDocFile instanceof File) {
      fd.append("visa_document", visaDocFile, visaDocFile.name);
    }
    if (brpDocFile instanceof File) {
      fd.append("brp_document", brpDocFile, brpDocFile.name);
    }
    if (contractDocFile instanceof File) {
      fd.append("contract_document", contractDocFile, contractDocFile.name);
    }

    // choose endpoint
    if (isEdit && initialData?.id) {
      fd.append("id", initialData?.id);
      dispatch(employeeActions.updateEmployee(fd, initialData?.id, onHide));
    } else {
      dispatch(employeeActions.createEmployee(fd, onHide));
    }
  };

  const title = isAdd
    ? "Add Employee"
    : isEdit
    ? "Edit Employee"
    : "View Employee";

  return (
    <Modal show={show} onHide={onHide} size="lg" backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title>{title}</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <form onSubmit={handleSubmit(onSubmit)}>
          <fieldset disabled={isView}>
            <div className="row g-3">
              {/* LEFT SIDE: all text/date fields */}
              <div className="col-lg-8 pe-lg-5">
                <div className="row g-3">
                  {/* fullName + email */}
                  <div className="col-md-6">
                    <div className="form-floating">
                      <input
                        id="name"
                        className="form-control"
                        {...register("fullName", {
                          required: "Full name is required",
                        })}
                      />
                      <label htmlFor="name">Full Name</label>
                    </div>
                    {errors.fullName && (
                      <small className="text-danger">
                        {errors.fullName.message}
                      </small>
                    )}
                  </div>
                  <div className="col-md-6">
                    <div className="form-floating">
                      <input
                        id="email"
                        type="email"
                        className="form-control"
                        {...register("email", {
                          required: "Email is required",
                        })}
                      />
                      <label htmlFor="email" className="form-label">
                        Email
                      </label>
                    </div>
                    {errors.email && (
                      <small className="text-danger">
                        {errors.email.message}
                      </small>
                    )}
                  </div>

                  {/* phone + NI number */}
                  <div className="col-md-6">
                    <Controller
                      name="phone_number"
                      control={control}
                      rules={{
                        required: "The field is required",
                        validate: (val) =>
                          (val && val.length >= 8) || "Invalid number",
                      }}
                      render={({ field: { value, onChange } }) => (
                        <PhoneInput
                          country={countryCode}
                          value={(value ?? "").toString()}
                          onChange={(val, country) => {
                            onChange(val);
                            if (country?.countryCode)
                              setCountryCode(country.countryCode);
                          }}
                          inputProps={{
                            name: "phone_number",
                            required: true,
                            autoFocus: false,
                          }}
                          placeholder="Enter phone number"
                          enableSearch
                          disableSearchIcon={false}
                          inputStyle={{ width: "100%" }}
                          buttonStyle={{
                            borderTopLeftRadius: 6,
                            borderBottomLeftRadius: 6,
                          }}
                          containerStyle={{ width: "100%" }}
                          disabled={isView}
                        />
                      )}
                    />
                    {errors.phone_number && (
                      <small className="text-danger">
                        {errors.phone_number.message}
                      </small>
                    )}
                  </div>
                  <div className="col-md-6">
                    <div className="form-floating">
                      <input
                        id="nin"
                        className="form-control"
                        type="text"
                        {...register("national_insurance_number")}
                      />
                      <label htmlFor="nin" className="form-label">
                        National Insurance Number
                      </label>
                    </div>
                  </div>

                  {/* passport */}
                  <div className="col-md-6">
                    <div className="form-floating">
                      <input
                        id="pn"
                        className="form-control"
                        type="text"
                        {...register("passport_number")}
                      />
                      <label htmlFor="pn" className="form-label">
                        Passport Number
                      </label>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="form-floating">
                      <input
                        id="pi"
                        type="date"
                        max="9999-12-31"
                        className="form-control"
                        {...register("passport_issue_date")}
                      />
                      <label htmlFor="pi" className="form-label">
                        Passport Issue
                      </label>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="form-floating">
                      <input
                        id="pe"
                        type="date"
                        max="9999-12-31"
                        className="form-control"
                        {...register("passport_expiry_date")}
                      />
                      <label htmlFor="pe" className="form-label">
                        Passport Expiry
                      </label>
                    </div>
                  </div>

                  {/* visa */}
                  <div className="col-md-6">
                    <div className="form-floating">
                      <input
                        id="vt"
                        className="form-control"
                        {...register("visa_type")}
                      />
                      <label htmlFor="vt" className="form-label">
                        Visa Type
                      </label>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="form-floating">
                      <input
                        id="vn"
                        type="text"
                        className="form-control"
                        {...register("visa_number")}
                      />
                      <label htmlFor="vn" className="form-label">
                        Visa Number
                      </label>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="form-floating">
                      <input
                        id="vi"
                        type="date"
                        max="9999-12-31"
                        className="form-control"
                        {...register("visa_issue_date")}
                      />
                      <label htmlFor="vi" className="form-label">
                        Visa Issue
                      </label>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="form-floating">
                      <input
                        id="ve"
                        type="date"
                        max="9999-12-31"
                        className="form-control"
                        {...register("visa_expiry_date")}
                      />
                      <label htmlFor="ve" className="form-label">
                        Visa Expiry
                      </label>
                    </div>
                  </div>

                  {/* BRP */}
                  <div className="col-md-6">
                    <div className="form-floating">
                      <input
                        id="brp"
                        type="text"
                        className="form-control"
                        {...register("biometric_residence_permit_number")}
                      />
                      <label htmlFor="brp" className="form-label">
                        BRP / Biometric Residence Permit
                      </label>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="form-floating">
                      <input
                        id="brpe"
                        type="date"
                        max="9999-12-31"
                        className="form-control"
                        {...register("brp_expiry_date")}
                      />
                      <label htmlFor="brpe" className="form-label">
                        BRP Expiry
                      </label>
                    </div>
                  </div>

                  {/* Contract dates */}
                  <div className="col-md-6">
                    <div className="form-floating">
                      <input
                        id="cs"
                        type="date"
                        max="9999-12-31"
                        className="form-control"
                        {...register("contract_start_date")}
                      />
                      <label htmlFor="cs" className="form-label">
                        Contract Start
                      </label>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="form-floating">
                      <input
                        id="ce"
                        type="date"
                        max="9999-12-31"
                        className="form-control"
                        {...register("contract_end_date")}
                      />
                      <label htmlFor="ce" className="form-label">
                        Contract End
                      </label>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="form-floating">
                      <input
                        id="code"
                        type="text"
                        className="form-control"
                        {...register("code")}
                      />
                      <label htmlFor="code" className="form-label">
                        Share Code
                      </label>
                    </div>
                  </div>

                  {/* Address */}
                  <div className="col-12">
                    <div className="form-floating">
                      <textarea
                        id="address"
                        className="form-control"
                        style={{ height: 90 }}
                        placeholder="Address"
                        {...register("address")}
                      />
                      <label htmlFor="address" className="form-label">
                        Address <span className="text-muted">(optional)</span>
                      </label>
                    </div>
                  </div>
                </div>
              </div>

              {/* RIGHT SIDE: file uploads */}
              <div className="col-lg-4 right-panel ps-5 pe-5">
                {/* profile pic */}
                <div className="mb-3 text-center">
                  <div
                    style={{
                      width: 110,
                      height: 110,
                      borderRadius: "50%",
                      background: "#eef5ff",
                      overflow: "hidden",
                      margin: "0 auto 10px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    {previewPic ? (
                      <img
                        src={previewPic}
                        alt="profile"
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                        }}
                      />
                    ) : (
                      <i
                        className="bi bi-person-fill"
                        style={{ fontSize: 56 }}
                      />
                    )}
                  </div>
                  {!isView && (
                    <div className="form-floating">
                      <input
                        id="pp"
                        type="file"
                        accept="image/*"
                        className="form-control"
                        onChange={(e) => {
                          const f = e.target.files?.[0];
                          setProfilePicFile(f || null);
                          if (f) {
                            const url = URL.createObjectURL(f);
                            setPreviewPic(url);
                          }
                        }}
                      />
                      <label htmlFor="pp">
                        {" "}
                        Profile Pic 
                        {/* <span className="text-danger fs-5">*</span> */}
                      </label>
                    </div>
                  )}
                </div>

                {/* Passport Document */}
                <DocUploadField
                  key={`passport-${mode}`}
                  label="Passport Document"
                  inputId="pd"
                  existingUrl={
                    !isAdd && initialData?.passport_document_url
                      ? process.env.REACT_APP_STORAGE_URL + initialData.passport_document_url
                      : null
                  }
                  newFile={passportDocFile}
                  onFileChange={setPassportDocFile}
                  disabled={isView}
                />

                {/* Visa Document */}
                <DocUploadField
                  key={`visa-${mode}`}
                  label="Visa Document"
                  inputId="vd"
                  existingUrl={
                    !isAdd && initialData?.visa_document_url
                      ? process.env.REACT_APP_STORAGE_URL + initialData.visa_document_url
                      : null
                  }
                  newFile={visaDocFile}
                  onFileChange={setVisaDocFile}
                  disabled={isView}
                />

                {/* BRP Document */}
                <DocUploadField
                  key={`brp-${mode}`}
                  label="BRP Document"
                  inputId="brpd"
                  existingUrl={
                    !isAdd && initialData?.brp_document_url
                      ? process.env.REACT_APP_STORAGE_URL + initialData.brp_document_url
                      : null
                  }
                  newFile={brpDocFile}
                  onFileChange={setBrpDocFile}
                  disabled={isView}
                />

                {/* Contract Document */}
                <DocUploadField
                  key={`contract-${mode}`}
                  label="Contract Document"
                  inputId="cd"
                  existingUrl={
                    !isAdd && initialData?.contract_document_url
                      ? process.env.REACT_APP_STORAGE_URL + initialData.contract_document_url
                      : null
                  }
                  newFile={contractDocFile}
                  onFileChange={setContractDocFile}
                  disabled={isView}
                />
              </div>
            </div>
          </fieldset>

          {/* footer */}
          <div className="d-flex justify-content-end footer-modal">
            <Button
              variant="outline-secondary"
              onClick={onHide}
              className="btn-footer"
            >
              {isView ? "Close" : "Cancel"}
            </Button>
            {!isView && (
              <Button type="submit" variant="success" className="btn-footer">
                {isEdit ? "Save Changes" : "Create Employee"}
              </Button>
            )}
          </div>
        </form>
      </Modal.Body>
    </Modal>
  );
}
